import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { MissionCreateModal } from '@/components/missions/MissionCreateModal';
import { 
  Building2, Briefcase, MessageSquare, Users, Settings, Bell, Plus,
  ChevronRight, Clock, CheckCircle, XCircle, Eye, Send,
  Calendar, MapPin, Euro, User, Loader2, FileText, Crown, CreditCard,
  Download, ArrowUpRight, Receipt, Zap, Heart, MessageCircle, Monitor, Trash2,
  Star
} from 'lucide-react';
import { toast } from 'sonner';
import EntrepriseProfileEdit from '@/components/profile/EntrepriseProfileEdit';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Get stored auth data
const getAuthData = () => {
  try {
    const token = localStorage.getItem('sfretire_token');
    const user = JSON.parse(localStorage.getItem('sfretire_user') || 'null');
    return { token, user };
  } catch {
    return { token: null, user: null };
  }
};

// Status badge component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
    approved: { label: 'Validé', color: 'bg-green-100 text-green-800', icon: CheckCircle },
    rejected: { label: 'Refusé', color: 'bg-red-100 text-red-800', icon: XCircle },
    draft: { label: 'Brouillon', color: 'bg-gray-100 text-gray-800', icon: FileText },
    published: { label: 'Publiée', color: 'bg-green-100 text-green-800', icon: CheckCircle },
    sent: { label: 'Nouvelle', color: 'bg-blue-100 text-blue-800', icon: Send },
    viewed: { label: 'Consultée', color: 'bg-purple-100 text-purple-800', icon: Eye },
    shortlisted: { label: 'Présélectionné', color: 'bg-indigo-100 text-indigo-800', icon: CheckCircle },
    accepted: { label: 'Acceptée', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  };

  const config = statusConfig[status] || statusConfig.pending;
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
      <Icon size={12} />
      {config.label}
    </span>
  );
};

// Mission card component
const MissionCard = ({ mission, onViewDetails }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-card border border-border rounded-xl p-4 hover:shadow-md transition-shadow"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h4 className="font-medium text-foreground line-clamp-1">{mission.title}</h4>
          <p className="text-sm text-muted-foreground line-clamp-1 mt-1">{mission.description}</p>
        </div>
        <StatusBadge status={mission.status} />
      </div>
      
      <div className="flex items-center gap-4 text-xs text-muted-foreground mb-3">
        <span className="flex items-center gap-1">
          <Users size={12} />
          {mission.applications_count || 0} candidats
        </span>
        <span className="flex items-center gap-1">
          <Eye size={12} />
          {mission.views_count || 0} vues
        </span>
        {mission.budget_max && (
          <span className="flex items-center gap-1">
            <Euro size={12} />
            {mission.budget_max}€
          </span>
        )}
      </div>

      <div className="flex gap-2">
        <Button 
          variant="ghost" 
          size="sm" 
          className="flex-1"
          onClick={() => onViewDetails(mission)}
        >
          Voir détails
        </Button>
        <Button 
          variant="outline" 
          size="sm" 
          className="flex-1"
        >
          Candidatures
        </Button>
      </div>
    </motion.div>
  );
};

// Application received card
const ReceivedApplicationCard = ({ application, onUpdateStatus, onContact, onReview, hasReview }) => {
  const canReview = application.status === 'accepted' || application.status === 'completed';
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-card border border-border rounded-xl p-4 hover:shadow-md transition-shadow"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
          <User size={20} className="text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-foreground truncate">
              {application.retraite_name || 'Candidat'}
            </h4>
            <StatusBadge status={application.status} />
          </div>
          <p className="text-sm text-muted-foreground truncate">
            Pour: {application.mission_title || 'Mission'}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {new Date(application.created_at).toLocaleDateString('fr-FR')}
          </p>
        </div>
      </div>

      {application.cover_letter && (
        <p className="text-sm text-muted-foreground line-clamp-2 mt-3 pl-13">
          {application.cover_letter}
        </p>
      )}

      <div className="flex gap-2 mt-3">
        <Button variant="outline" size="sm" className="flex-1">
          Voir profil
        </Button>
        <Button 
          variant="ghost" 
          size="sm" 
          className="flex-1"
          onClick={() => onContact && onContact(application)}
        >
          <MessageSquare size={14} className="mr-1" />
          Contacter
        </Button>
        {canReview && !hasReview && (
          <Button
            variant="ghost"
            size="sm"
            className="text-amber-500 hover:text-amber-600 hover:bg-amber-50"
            onClick={() => onReview && onReview(application)}
            data-testid={`review-btn-${application.id}`}
          >
            <Star size={14} className="mr-1" />
            Avis
          </Button>
        )}
        {canReview && hasReview && (
          <span className="flex items-center text-xs text-amber-500 px-2" data-testid={`review-done-${application.id}`}>
            <Star size={12} className="fill-amber-400 mr-1" /> Avis publié
          </span>
        )}
      </div>
    </motion.div>
  );
};

// Conversation card component
const ConversationCard = ({ conversation, onClick }) => {
  const hasUnread = conversation.unread_entreprise > 0;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={onClick}
      className={`bg-card border rounded-xl p-4 cursor-pointer hover:shadow-md transition-all ${
        hasUnread ? 'border-primary/50 bg-primary/5' : 'border-border'
      }`}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
          <User size={20} className="text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-foreground truncate">
              {conversation.other_party?.name || 'Contact'}
            </h4>
            {hasUnread && (
              <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center">
                {conversation.unread_entreprise}
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground truncate mt-1">
            {conversation.last_message_preview || 'Aucun message'}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {conversation.last_message_at 
              ? new Date(conversation.last_message_at).toLocaleDateString('fr-FR')
              : ''
            }
          </p>
        </div>
      </div>
    </motion.div>
  );
};

// Review Modal component
const ReviewModal = ({ isOpen, onClose, application, token, onSuccess }) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen || !application) return null;

  const handleSubmit = async () => {
    if (rating < 1) {
      toast.error('Veuillez sélectionner une note');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/reviews`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          retraite_id: application.retraite_id,
          mission_id: application.mission_id,
          rating,
          comment: comment.trim(),
        }),
      });
      if (res.status === 409) {
        toast.error('Vous avez déjà laissé un avis pour cet expert sur cette mission');
        onClose();
        return;
      }
      if (!res.ok) throw new Error();
      toast.success('Avis publié avec succès !');
      onSuccess && onSuccess();
      onClose();
    } catch {
      toast.error("Erreur lors de l'envoi de l'avis");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-card border border-border rounded-2xl w-full max-w-md p-6 shadow-xl"
        onClick={e => e.stopPropagation()}
        data-testid="review-modal"
      >
        <h3 className="text-lg font-semibold text-foreground mb-1">
          Laisser un avis
        </h3>
        <p className="text-sm text-muted-foreground mb-5">
          Pour <span className="font-medium text-foreground">{application.retraite_name || 'cet expert'}</span>
          {application.mission_title && (
            <> &mdash; {application.mission_title}</>
          )}
        </p>

        {/* Star selection */}
        <div className="flex items-center gap-1 mb-5" data-testid="rating-stars">
          {[1, 2, 3, 4, 5].map(i => (
            <button
              key={i}
              onMouseEnter={() => setHoverRating(i)}
              onMouseLeave={() => setHoverRating(0)}
              onClick={() => setRating(i)}
              className="p-0.5 transition-transform hover:scale-110"
              data-testid={`star-${i}`}
            >
              <Star
                size={32}
                className={`transition-colors ${
                  i <= (hoverRating || rating)
                    ? 'text-amber-400 fill-amber-400'
                    : 'text-gray-200'
                }`}
              />
            </button>
          ))}
          <span className="ml-2 text-sm text-muted-foreground">
            {rating > 0 && ['', 'Décevant', 'Moyen', 'Bien', 'Très bien', 'Excellent'][rating]}
          </span>
        </div>

        {/* Comment */}
        <textarea
          value={comment}
          onChange={e => setComment(e.target.value)}
          placeholder="Partagez votre expérience (optionnel)..."
          className="w-full h-24 px-3 py-2 rounded-lg border border-input bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary mb-4"
          data-testid="review-comment"
        />

        <div className="flex gap-2">
          <Button className="flex-1" onClick={handleSubmit} disabled={submitting || rating < 1} data-testid="submit-review-btn">
            {submitting ? <Loader2 size={16} className="animate-spin mr-2" /> : <Star size={16} className="mr-2" />}
            Publier l&apos;avis
          </Button>
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
        </div>
      </motion.div>
    </div>
  );
};

// Main Enterprise Dashboard
const EntrepriseDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [editing, setEditing] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [profile, setProfile] = useState(null);
  const [missions, setMissions] = useState([]);
  const [applications, setApplications] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [myReviews, setMyReviews] = useState(new Set());
  const [stats, setStats] = useState({
    totalMissions: 0,
    publishedMissions: 0,
    totalApplications: 0,
    unreadMessages: 0
  });

  const token = localStorage.getItem('sfretire_token');

  useEffect(() => {
    const storedUser = localStorage.getItem('sfretire_user');
    let user = null;
    try { user = JSON.parse(storedUser); } catch {}

    if (!token || !user) {
      navigate('/');
      return;
    }

    if (user.role !== 'entreprise' && user.user_type !== 'entreprise') {
      navigate('/dashboard/retraite');
      return;
    }

    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle Stripe payment return
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const payment = searchParams.get('payment');
    if (sessionId && payment === 'success' && token) {
      const pollStatus = async (attempts = 0) => {
        if (attempts >= 5) return;
        try {
          const res = await fetch(`${API_URL}/api/payments/status/${sessionId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const data = await res.json();
          if (data.payment_status === 'paid') {
            toast.success('Paiement réussi ! Votre abonnement est activé.');
            fetchDashboardData();
            return;
          }
          setTimeout(() => pollStatus(attempts + 1), 2000);
        } catch (err) {
          console.error('Payment status check failed:', err);
        }
      };
      pollStatus();
    }
  }, [searchParams]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const headers = { 'Authorization': `Bearer ${token}` };

      // Fetch user profile
      const meRes = await fetch(`${API_URL}/api/auth/me`, { headers });
      let currentProfile = null;
      if (meRes.ok) {
        const meData = await meRes.json();
        currentProfile = meData.user?.profile;
        setProfile(currentProfile);
      }

      // Fetch missions
      const missionsRes = await fetch(`${API_URL}/api/missions?entreprise_id=${currentProfile?.id || ''}`, { headers });
      if (missionsRes.ok) {
        const missionsData = await missionsRes.json();
        setMissions(missionsData.items || []);
        setStats(prev => ({
          ...prev,
          totalMissions: missionsData.total || 0,
          publishedMissions: (missionsData.items || []).filter(m => m.status === 'published').length
        }));
      }

      // Fetch applications
      const appsRes = await fetch(`${API_URL}/api/applications`, { headers });
      if (appsRes.ok) {
        const appsData = await appsRes.json();
        setApplications(appsData.items || []);
        setStats(prev => ({
          ...prev,
          totalApplications: appsData.total || 0
        }));
      }

      // Fetch conversations
      const convRes = await fetch(`${API_URL}/api/conversations`, { headers });
      if (convRes.ok) {
        const convData = await convRes.json();
        setConversations(convData.items || []);
        const unread = (convData.items || []).reduce((sum, c) => sum + (c.unread_entreprise || 0), 0);
        setStats(prev => ({ ...prev, unreadMessages: unread }));
      }

      // Fetch subscription status
      const subRes = await fetch(`${API_URL}/api/payments/subscription`, { headers });
      if (subRes.ok) {
        const subData = await subRes.json();
        setSubscription(subData);
      }

      // Fetch transactions
      const txRes = await fetch(`${API_URL}/api/payments/transactions`, { headers });
      if (txRes.ok) {
        const txData = await txRes.json();
        setTransactions(txData.items || []);
      }

      // Fetch favorites
      const favRes = await fetch(`${API_URL}/api/favorites`, { headers });
      if (favRes.ok) {
        const favData = await favRes.json();
        setFavorites(favData.items || []);
      }

      // Fetch my reviews (to know which experts were already reviewed)
      const revRes = await fetch(`${API_URL}/api/reviews/my`, { headers });
      if (revRes.ok) {
        const revData = await revRes.json();
        const reviewKeys = new Set(revData.items.map(r => `${r.retraite_id}_${r.mission_id || 'direct'}`));
        setMyReviews(reviewKeys);
      }

    } catch (error) {
      console.error('Dashboard fetch error:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleMissionCreated = (mission) => {
    setMissions(prev => [mission, ...prev]);
    setStats(prev => ({
      ...prev,
      totalMissions: prev.totalMissions + 1,
      publishedMissions: mission.status === 'published' ? prev.publishedMissions + 1 : prev.publishedMissions
    }));
  };

  const handleContactCandidate = async (application) => {
    try {
      const response = await fetch(`${API_URL}/api/conversations/from-application`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ application_id: application.id })
      });

      if (response.ok) {
        const data = await response.json();
        navigate(`/messages/${data.conversation.id}`);
      } else {
        toast.error('Erreur lors de la création de la conversation');
      }
    } catch (error) {
      console.error('Error creating conversation:', error);
      toast.error('Erreur de connexion');
    }
  };

  const handleRemoveFavorite = async (expertId) => {
    try {
      const res = await fetch(`${API_URL}/api/favorites/${expertId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setFavorites(prev => prev.filter(f => f.id !== expertId));
        toast.success('Expert retiré des favoris');
      }
    } catch {
      toast.error('Erreur lors de la suppression du favori');
    }
  };

  const handleContactFavorite = async (expert) => {
    try {
      const res = await fetch(`${API_URL}/api/conversations/direct`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ retraite_id: expert.id })
      });
      if (res.ok) {
        const data = await res.json();
        navigate(`/messages/${data.conversation.id}`);
      } else {
        toast.error('Erreur lors de la création de la conversation');
      }
    } catch {
      toast.error('Erreur de connexion');
    }
  };

  const tabs = [
    { id: 'overview', label: 'Vue d\'ensemble', icon: Building2 },
    { id: 'missions', label: 'Mes missions', icon: Briefcase },
    { id: 'applications', label: 'Candidatures', icon: Users },
    { id: 'favorites', label: 'Favoris', icon: Heart, count: favorites.length },
    { id: 'messages', label: 'Messages', icon: MessageSquare },
    { id: 'billing', label: 'Abonnement', icon: CreditCard },
    { id: 'profile', label: 'Mon entreprise', icon: Settings },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-24 flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4 lg:px-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-serif font-bold text-foreground">
                {profile?.company_name || 'Mon Entreprise'}
              </h1>
              <p className="text-muted-foreground mt-2">
                Gérez vos missions et trouvez les meilleurs experts
              </p>
            </div>
            <Button variant="gold" className="flex items-center gap-2" onClick={() => setShowCreateModal(true)}>
              <Plus size={18} />
              Nouvelle mission
            </Button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-card border border-border rounded-xl p-5"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Briefcase className="text-primary" size={24} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats.totalMissions}</p>
                  <p className="text-sm text-muted-foreground">Missions totales</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-card border border-border rounded-xl p-5"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="text-green-600" size={24} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats.publishedMissions}</p>
                  <p className="text-sm text-muted-foreground">Publiées</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-card border border-border rounded-xl p-5"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                  <Users className="text-purple-600" size={24} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats.totalApplications}</p>
                  <p className="text-sm text-muted-foreground">Candidatures</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-card border border-border rounded-xl p-5"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <MessageSquare className="text-blue-600" size={24} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats.unreadMessages}</p>
                  <p className="text-sm text-muted-foreground">Messages non lus</p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 border-b border-border mb-6 overflow-x-auto pb-px">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px ${
                  activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon size={18} />
                {tab.label}
                {tab.count > 0 && (
                  <span className="ml-1 w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-semibold">
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <div className="space-y-8">
              {/* Subscription Banner */}
              {subscription?.status !== 'active' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-5 flex items-center justify-between"
                  data-testid="subscription-banner"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                      <Crown size={24} className="text-amber-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground">Activez votre abonnement</h3>
                      <p className="text-sm text-muted-foreground">
                        Publiez des missions et contactez les experts retraités
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="gold"
                    onClick={() => navigate('/tarifs')}
                    data-testid="subscribe-cta-btn"
                  >
                    <CreditCard size={18} className="mr-2" />
                    Voir les tarifs
                  </Button>
                </motion.div>
              )}
              {subscription?.status === 'active' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-5 flex items-center gap-4"
                  data-testid="subscription-active-banner"
                >
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle size={20} className="text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">Abonnement actif</h3>
                    <p className="text-sm text-muted-foreground">
                      Plan {subscription.plan?.replace('_', ' ')} &mdash; Activé le {subscription.activated_at ? new Date(subscription.activated_at).toLocaleDateString('fr-FR') : ''}
                    </p>
                  </div>
                </motion.div>
              )}

              <div className="grid lg:grid-cols-2 gap-8">
              {/* Recent Missions */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-foreground">Missions récentes</h2>
                  <Button variant="ghost" size="sm" onClick={() => setActiveTab('missions')}>
                    Voir tout <ChevronRight size={16} />
                  </Button>
                </div>
                <div className="space-y-3">
                  {missions.slice(0, 3).map(mission => (
                    <MissionCard 
                      key={mission.id} 
                      mission={mission}
                      onViewDetails={() => {}}
                    />
                  ))}
                  {missions.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <Briefcase className="mx-auto mb-2 opacity-50" size={32} />
                      <p>Aucune mission créée</p>
                      <Button variant="outline" size="sm" className="mt-3">
                        <Plus size={16} className="mr-2" />
                        Créer une mission
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Recent Applications */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-foreground">Candidatures reçues</h2>
                  <Button variant="ghost" size="sm" onClick={() => setActiveTab('applications')}>
                    Voir tout <ChevronRight size={16} />
                  </Button>
                </div>
                <div className="space-y-3">
                  {applications.slice(0, 3).map(app => (
                    <ReceivedApplicationCard 
                      key={app.id} 
                      application={app}
                      onUpdateStatus={() => {}}
                      onContact={handleContactCandidate}
                      onReview={(a) => setReviewTarget(a)}
                      hasReview={myReviews.has(`${app.retraite_id}_${app.mission_id || 'direct'}`)}
                    />
                  ))}
                  {applications.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <Users className="mx-auto mb-2 opacity-50" size={32} />
                      <p>Aucune candidature reçue</p>
                    </div>
                  )}
                </div>
              </div>
              </div>
            </div>
          )}

          {activeTab === 'missions' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-foreground">
                  Toutes mes missions ({missions.length})
                </h2>
                <Button variant="gold" size="sm" onClick={() => setShowCreateModal(true)}>
                  <Plus size={16} className="mr-2" />
                  Nouvelle mission
                </Button>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {missions.map(mission => (
                  <MissionCard 
                    key={mission.id} 
                    mission={mission}
                    onViewDetails={() => {}}
                  />
                ))}
              </div>
              {missions.length === 0 && (
                <div className="text-center py-16 text-muted-foreground">
                  <Briefcase className="mx-auto mb-4 opacity-50" size={48} />
                  <p className="text-lg">Vous n&apos;avez pas encore créé de mission</p>
                  <p className="text-sm mt-2">Publiez votre première mission pour trouver des experts</p>
                  <Button variant="gold" className="mt-4" onClick={() => setShowCreateModal(true)}>
                    <Plus size={16} className="mr-2" />
                    Créer ma première mission
                  </Button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'applications' && (
            <div>
              <h2 className="text-lg font-semibold text-foreground mb-6">
                Candidatures reçues ({applications.length})
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {applications.map(app => (
                  <ReceivedApplicationCard 
                    key={app.id} 
                    application={app}
                    onUpdateStatus={() => {}}
                    onContact={handleContactCandidate}
                    onReview={(a) => setReviewTarget(a)}
                    hasReview={myReviews.has(`${app.retraite_id}_${app.mission_id || 'direct'}`)}
                  />
                ))}
              </div>
              {applications.length === 0 && (
                <div className="text-center py-16 text-muted-foreground">
                  <Users className="mx-auto mb-4 opacity-50" size={48} />
                  <p className="text-lg">Aucune candidature reçue</p>
                  <p className="text-sm mt-2">Publiez des missions pour recevoir des candidatures</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'favorites' && (
            <div data-testid="favorites-tab">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-foreground">
                  Mes experts favoris ({favorites.length})
                </h2>
                <Button variant="outline" size="sm" asChild>
                  <Link to="/recherche-experts" className="flex items-center gap-2">
                    <Users size={16} />
                    Rechercher des experts
                  </Link>
                </Button>
              </div>

              {favorites.length > 0 ? (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {favorites.map(expert => (
                    <motion.div
                      key={expert.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-card border border-border rounded-xl overflow-hidden hover:border-primary/30 hover:shadow-lg transition-all"
                      data-testid={`fav-card-${expert.id}`}
                    >
                      <div className="p-5">
                        <div className="flex items-start gap-3.5 mb-3">
                          {expert.photo_url ? (
                            <img src={expert.photo_url} alt="" className="w-12 h-12 rounded-xl object-cover flex-shrink-0" />
                          ) : (
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center flex-shrink-0">
                              <User size={20} className="text-primary" />
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <h3 className="font-semibold text-foreground truncate">{expert.name}</h3>
                            <p className="text-sm text-muted-foreground truncate">{expert.title || 'Expert'}</p>
                            {expert.location && (
                              <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                                <MapPin size={11} /> {expert.location}
                              </p>
                            )}
                          </div>
                          {expert.hourly_rate && (
                            <div className="text-right flex-shrink-0">
                              <p className="text-lg font-bold text-foreground">{expert.hourly_rate}&euro;</p>
                              <p className="text-xs text-muted-foreground">/heure</p>
                            </div>
                          )}
                        </div>

                        {expert.skills?.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mb-3">
                            {expert.skills.slice(0, 4).map((skill, i) => (
                              <span key={i} className="px-2 py-0.5 bg-primary/8 text-primary text-xs rounded-md font-medium">
                                {skill}
                              </span>
                            ))}
                            {expert.skills.length > 4 && (
                              <span className="px-2 py-0.5 bg-secondary text-muted-foreground text-xs rounded-md">
                                +{expert.skills.length - 4}
                              </span>
                            )}
                          </div>
                        )}

                        <div className="flex gap-2 mt-3 pt-3 border-t border-border">
                          <Button
                            size="sm"
                            className="flex-1"
                            onClick={() => handleContactFavorite(expert)}
                            data-testid={`fav-contact-${expert.id}`}
                          >
                            <MessageCircle size={14} className="mr-1.5" />
                            Contacter
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                            onClick={() => handleRemoveFavorite(expert.id)}
                            data-testid={`fav-remove-${expert.id}`}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 text-muted-foreground">
                  <Heart className="mx-auto mb-4 opacity-50" size={48} />
                  <p className="text-lg">Aucun expert en favoris</p>
                  <p className="text-sm mt-2">Explorez les experts et ajoutez-les à vos favoris</p>
                  <Button variant="outline" className="mt-4" asChild>
                    <Link to="/recherche-experts">Rechercher des experts</Link>
                  </Button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'messages' && (
            <div>
              <h2 className="text-lg font-semibold text-foreground mb-6">
                Mes conversations ({conversations.length})
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {conversations.map(conv => (
                  <ConversationCard 
                    key={conv.id} 
                    conversation={conv}
                    onClick={() => {}}
                  />
                ))}
              </div>
              {conversations.length === 0 && (
                <div className="text-center py-16 text-muted-foreground">
                  <MessageSquare className="mx-auto mb-4 opacity-50" size={48} />
                  <p className="text-lg">Pas de conversation pour le moment</p>
                  <p className="text-sm mt-2">Contactez des experts pour démarrer une conversation</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'billing' && (
            <div className="space-y-8" data-testid="billing-tab">
              {/* Subscription Status Card */}
              <div className="bg-card border border-border rounded-xl overflow-hidden">
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                        subscription?.status === 'active' 
                          ? 'bg-green-100' 
                          : 'bg-amber-100'
                      }`}>
                        {subscription?.status === 'active' 
                          ? <Crown size={28} className="text-green-600" />
                          : <Zap size={28} className="text-amber-600" />
                        }
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-foreground">
                          {subscription?.status === 'active' ? 'Abonnement actif' : 'Aucun abonnement'}
                        </h2>
                        {subscription?.status === 'active' ? (
                          <div className="space-y-1 mt-1">
                            <p className="text-sm text-muted-foreground">
                              Plan <span className="font-medium text-foreground capitalize">{subscription.plan?.replace('_', ' ')}</span>
                              {subscription.interval && (
                                <span> &mdash; {subscription.interval === 'monthly' ? 'Mensuel' : 'Annuel'}</span>
                              )}
                            </p>
                            {subscription.activated_at && (
                              <p className="text-xs text-muted-foreground">
                                Activé le {new Date(subscription.activated_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                              </p>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground mt-1">
                            Souscrivez un abonnement pour publier des missions et contacter les experts
                          </p>
                        )}
                      </div>
                    </div>
                    <Button
                      variant={subscription?.status === 'active' ? 'outline' : 'gold'}
                      onClick={() => navigate('/tarifs')}
                      data-testid="manage-subscription-btn"
                    >
                      {subscription?.status === 'active' ? (
                        <>Changer de plan <ArrowUpRight size={16} className="ml-1" /></>
                      ) : (
                        <>Voir les tarifs <ArrowUpRight size={16} className="ml-1" /></>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Plan features summary */}
                {subscription?.status === 'active' && subscription?.plan && (
                  <div className="border-t border-border bg-secondary/30 px-6 py-4">
                    <div className="flex flex-wrap gap-4 text-sm">
                      {subscription.plan.startsWith('starter') ? (
                        <>
                          <span className="flex items-center gap-1.5 text-muted-foreground">
                            <CheckCircle size={14} className="text-green-500" /> 5 missions actives
                          </span>
                          <span className="flex items-center gap-1.5 text-muted-foreground">
                            <CheckCircle size={14} className="text-green-500" /> Messagerie illimitée
                          </span>
                          <span className="flex items-center gap-1.5 text-muted-foreground">
                            <CheckCircle size={14} className="text-green-500" /> Support email
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="flex items-center gap-1.5 text-muted-foreground">
                            <CheckCircle size={14} className="text-green-500" /> Missions illimitées
                          </span>
                          <span className="flex items-center gap-1.5 text-muted-foreground">
                            <CheckCircle size={14} className="text-green-500" /> Messagerie prioritaire
                          </span>
                          <span className="flex items-center gap-1.5 text-muted-foreground">
                            <CheckCircle size={14} className="text-green-500" /> Support téléphonique
                          </span>
                          <span className="flex items-center gap-1.5 text-muted-foreground">
                            <CheckCircle size={14} className="text-green-500" /> Statistiques avancées
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Transactions History */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-foreground">Historique des paiements</h2>
                  <span className="text-sm text-muted-foreground">{transactions.length} transaction{transactions.length !== 1 ? 's' : ''}</span>
                </div>

                {transactions.length === 0 ? (
                  <div className="bg-card border border-border rounded-xl py-16 text-center">
                    <Receipt className="mx-auto mb-4 text-muted-foreground/40" size={48} />
                    <p className="text-lg text-muted-foreground">Aucune transaction</p>
                    <p className="text-sm text-muted-foreground mt-1">Vos paiements apparaîtront ici</p>
                  </div>
                ) : (
                  <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="transactions-table">
                    {/* Table header */}
                    <div className="hidden sm:grid grid-cols-12 gap-4 px-6 py-3 bg-secondary/50 text-xs font-semibold text-muted-foreground uppercase tracking-wider border-b border-border">
                      <div className="col-span-3">Date</div>
                      <div className="col-span-3">Type</div>
                      <div className="col-span-2">Montant</div>
                      <div className="col-span-2">Statut</div>
                      <div className="col-span-2 text-right">Reçu</div>
                    </div>

                    {/* Table rows */}
                    <div className="divide-y divide-border">
                      {transactions.map((tx) => {
                        const isPaid = tx.payment_status === 'paid';
                        const isPending = tx.payment_status === 'pending' || tx.status === 'initiated';
                        const isFailed = tx.payment_status === 'unpaid' || tx.status === 'expired';

                        const statusLabel = isPaid ? 'Payé' : isPending ? 'En attente' : 'Échoué';
                        const statusColor = isPaid 
                          ? 'bg-green-100 text-green-700' 
                          : isPending 
                            ? 'bg-yellow-100 text-yellow-700' 
                            : 'bg-red-100 text-red-700';

                        const typeLabel = tx.type === 'subscription' 
                          ? `Abonnement ${tx.plan_id?.replace('_', ' ') || ''}` 
                          : 'Commission mission';

                        return (
                          <div 
                            key={tx.session_id} 
                            className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 px-6 py-4 items-center hover:bg-secondary/30 transition-colors"
                            data-testid={`transaction-row-${tx.session_id}`}
                          >
                            {/* Date */}
                            <div className="col-span-3">
                              <div className="flex items-center gap-2 sm:block">
                                <span className="text-xs text-muted-foreground sm:hidden font-medium">Date:</span>
                                <div>
                                  <p className="text-sm font-medium text-foreground">
                                    {new Date(tx.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                                  </p>
                                  <p className="text-xs text-muted-foreground hidden sm:block">
                                    {new Date(tx.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* Type */}
                            <div className="col-span-3">
                              <div className="flex items-center gap-2">
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                  tx.type === 'subscription' ? 'bg-blue-100' : 'bg-purple-100'
                                }`}>
                                  {tx.type === 'subscription' 
                                    ? <Crown size={16} className="text-blue-600" />
                                    : <Briefcase size={16} className="text-purple-600" />
                                  }
                                </div>
                                <span className="text-sm text-foreground capitalize">{typeLabel}</span>
                              </div>
                            </div>

                            {/* Amount */}
                            <div className="col-span-2">
                              <span className="text-sm font-semibold text-foreground">
                                {tx.amount?.toLocaleString('fr-FR', { minimumFractionDigits: 2 })}&nbsp;&euro;
                              </span>
                            </div>

                            {/* Status */}
                            <div className="col-span-2">
                              <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${statusColor}`}>
                                {isPaid && <CheckCircle size={12} />}
                                {isPending && <Clock size={12} />}
                                {isFailed && <XCircle size={12} />}
                                {statusLabel}
                              </span>
                            </div>

                            {/* Receipt */}
                            <div className="col-span-2 text-right">
                              {isPaid && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-primary hover:text-primary/80"
                                  onClick={() => window.open(`${API_URL}/api/payments/receipt/${tx.session_id}?token=${token}`, '_blank')}
                                  data-testid={`receipt-btn-${tx.session_id}`}
                                >
                                  <Download size={14} className="mr-1" />
                                  Reçu
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Commission Info */}
              <div className="bg-card border border-border rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                    <Euro size={20} className="text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">Commission par mission</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Une commission de <strong>10%</strong> est appliquée sur le budget de chaque mission lors 
                      de la validation d&apos;une candidature. Elle couvre les frais de mise en relation et de gestion.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'profile' && (
            <div className="max-w-2xl">
              <h2 className="text-lg font-semibold text-foreground mb-6">Mon entreprise</h2>
              
              {/* Profile Status */}
              <div className="bg-card border border-border rounded-xl p-6 mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-foreground">Statut du compte</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      {profile?.validation_status === 'approved' 
                        ? 'Votre entreprise est validée'
                        : 'Votre entreprise est en cours de validation'
                      }
                    </p>
                  </div>
                  <StatusBadge status={profile?.validation_status || 'pending'} />
                </div>
              </div>

              {editing ? (
                <EntrepriseProfileEdit
                  profile={profile}
                  token={token}
                  onSaved={(updated) => {
                    setProfile(updated);
                    setEditing(false);
                  }}
                  onCancel={() => setEditing(false)}
                />
              ) : (
                <>
                  {/* Company Details */}
                  <div className="bg-card border border-border rounded-xl p-6 space-y-4">
                    <div className="flex items-center gap-4">
                      {profile?.logo_url ? (
                        <img src={profile.logo_url} alt="" className="w-16 h-16 rounded-lg object-cover" />
                      ) : (
                        <div className="w-16 h-16 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Building2 size={28} className="text-primary" />
                        </div>
                      )}
                      <div>
                        <h3 className="text-xl font-semibold text-foreground">
                          {profile?.company_name}
                        </h3>
                        <p className="text-muted-foreground">{profile?.sector}</p>
                      </div>
                    </div>

                    {profile?.description && (
                      <div className="pt-4 border-t border-border">
                        <p className="text-sm text-muted-foreground mb-1">Description</p>
                        <p className="text-sm text-foreground">{profile.description}</p>
                      </div>
                    )}

                    <div className="grid sm:grid-cols-2 gap-4 pt-4 border-t border-border">
                      <div>
                        <p className="text-sm text-muted-foreground">Contact</p>
                        <p className="font-medium">{profile?.contact_name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Email</p>
                        <p className="font-medium">{profile?.contact_email}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Téléphone</p>
                        <p className="font-medium">{profile?.contact_phone || 'Non renseigné'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Taille</p>
                        <p className="font-medium">{profile?.size || 'Non renseignée'}</p>
                      </div>
                      {profile?.siret && (
                        <div>
                          <p className="text-sm text-muted-foreground">SIRET</p>
                          <p className="font-medium">{profile.siret}</p>
                        </div>
                      )}
                      {profile?.website && (
                        <div>
                          <p className="text-sm text-muted-foreground">Site web</p>
                          <a href={profile.website} target="_blank" rel="noopener noreferrer" className="font-medium text-primary hover:underline">
                            {profile.website}
                          </a>
                        </div>
                      )}
                      {profile?.city && (
                        <div>
                          <p className="text-sm text-muted-foreground">Ville</p>
                          <p className="font-medium">{profile.city}{profile?.postal_code ? ` (${profile.postal_code})` : ''}</p>
                        </div>
                      )}
                    </div>

                    <Button variant="outline" className="w-full mt-4" onClick={() => setEditing(true)} data-testid="edit-profile-btn">
                      <Settings size={16} className="mr-2" />
                      Modifier les informations
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </main>

      <Footer />

      {/* Mission Create Modal */}
      <MissionCreateModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleMissionCreated}
      />

      {/* Review Modal */}
      <ReviewModal
        isOpen={!!reviewTarget}
        onClose={() => setReviewTarget(null)}
        application={reviewTarget}
        token={token}
        onSuccess={() => {
          if (reviewTarget) {
            setMyReviews(prev => {
              const next = new Set(prev);
              next.add(`${reviewTarget.retraite_id}_${reviewTarget.mission_id || 'direct'}`);
              return next;
            });
          }
        }}
      />
    </div>
  );
};

export default EntrepriseDashboard;
